import { XRButton } from "@react-three/xr";
import React from "react";
import IconCardboards from "../../Icons/IconCardboards";
import EnterFullscreen from "../../Icons/EnterFullscreen";
import { Asset, Scene } from "../../../Types";
import {
  UseAssetsQuery,
  UseDownloadAsset,
  UseGetTour,
} from "../../../api/calls/Queries.ts";

import * as Player from "../../Icons/Player.tsx";
import Headphones from "../../Icons/Headphones.tsx";
import {
  useCurrentSceneStore,
  useEditingElementStore,
  useViewerStore,
} from "../../../api/Zustand.ts";
import { MapIcon } from "../../Icons/left-side-bar-icons.tsx";
import { useVolumeStore } from "../../../state management/zustand/common-stores.ts";
import { getLanguageFile } from "../../common/GetLanguageFile.ts";
import { useViewerMapStore } from "../../../state management/zustand/viewer-stores.ts";

const ExpOverlay = (props: { handle: any; tourID: number }) => {
  const { data: audioList } = UseAssetsQuery(props.tourID, 3);

  const viewerMap = useViewerMapStore();

  const { data: tourData } = UseGetTour(props.tourID);

  const { viewer, setViewer } = useViewerStore();

  const { setEditingElem } = useEditingElementStore();
  const { currentSceneID } = useCurrentSceneStore();

  const currentScene = React.useMemo(() => {
    return tourData?.scenes.find((one) => one.id === currentSceneID);
  }, [currentSceneID, tourData?.scenes]);

  return (
    <>
      {currentScene?.audio && (
        <div style={audioCSS}>
          <AudioBtn audioList={audioList} currentScene={currentScene} />
        </div>
      )}

      {currentScene?.map && (
        <div style={mapCss}>
          <button
            className="map-btn"
            id={viewerMap.isOpen ? "active" : undefined}
            onClick={(e) => {
              e.stopPropagation();
              viewerMap.setOpen(currentScene.map!);
            }}
          >
            <MapIcon
              fill="#fff"
              width={"3rem"}
              height={"3rem"}
              style={{
                cursor: "pointer",
              }}
            />
          </button>
        </div>
      )}

      <div onChange={(e) => e.preventDefault()} className="exp-overlay">
        {viewer && (
          <>
            <button
              id="inner"
              onClick={() => {
                setEditingElem("scene", currentSceneID || -1);
                setViewer(false);
              }}
              className="back"
              title={getLanguageFile("experience.back_to_editor")}
            >
              <span>{getLanguageFile("experience.back_to_editor")}</span>
            </button>

            <hr />
          </>
        )}
        <XRButton
          mode={"VR"}
          sessionInit={{ optionalFeatures: ["hand-tracking"] }}
          style={{
            background: "none",
            padding: "0",
            margin: "0",
            pointerEvents: "none",
          }}
        >
          {(status) => (
            <div
              // disabled
              className={
                status === "unsupported"
                  ? "vr-button unsupported"
                  : status === "entered"
                  ? "vr-button on"
                  : "vr-button off"
              }
              title={
                status === "unsupported"
                  ? getLanguageFile("button_labels.vr_not_supported")
                  : status === "entered"
                  ? getLanguageFile("button_labels.vr_exit")
                  : getLanguageFile("button_labels.vr_enter")
              }
              style={{
                pointerEvents: status === "unsupported" ? "none" : "all",
                display: "flex",
                alignItems: "center",
                height: "100%",
                justifyContent: "center",
              }}
            >
              <IconCardboards
                color="white"
                height={"1.5rem"}
                width={"1.5rem"}
              />
            </div>
          )}
        </XRButton>

        <button
          className="round"
          onClick={() => {
            if (document.fullscreenElement) {
              document.exitFullscreen();
              return;
            }
            props.handle.enter();
          }}
          title={getLanguageFile("experience.enter_fullscreen")}
        >
          <EnterFullscreen color="white" />
        </button>
      </div>
    </>
  );
};

const AudioBtn = (props: {
  audioList: Asset[] | undefined;
  currentScene: Scene;
}) => {
  const key: string | undefined = React.useMemo(() => {
    return props.audioList?.find(
      (one) => one.assetId === props.currentScene?.audio
    )?.assetKeyFull;
  }, [props]);

  const { volume } = useVolumeStore();

  const [playing, setPlaying] = React.useState<boolean>(false);
  const [ended, setEnded] = React.useState<boolean>(false);
  const [time, setTime] = React.useState<number>(0);
  const [length, setLength] = React.useState<number>(0);

  const { data, isLoading } = UseDownloadAsset(key);

  const ref = React.useRef<HTMLAudioElement>(null);

  React.useEffect(() => {
    if (ref.current) ref.current.volume = volume;
  }, [volume]);

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          color: "#fff",
        }}
      >
        {ended ? (
          <Player.Replay
            style={{ cursor: "pointer" }}
            width={"3rem"}
            height={"3rem"}
            fill="#fff"
            stroke="#222"
            strokeWidth={".75px"}
            onClick={() => {
              if (ref.current) {
                ref.current.pause();
                ref.current.currentTime = 0;
              }
            }}
          />
        ) : !playing ? (
          <Headphones
            style={{ cursor: "pointer", position: "absolute" }}
            fill="#fff"
            width={"3rem"}
            height={"3rem"}
            stroke={"#222"}
            strokeWidth="35px"
            onClick={() => {
              if (ref.current) {
                if (playing) ref.current.pause();
                else ref.current.play();
              }
            }}
          />
        ) : (
          <Player.Pause
            stroke="#282828"
            style={{
              cursor: "pointer",
              position: "absolute",
            }}
            strokeWidth={"1px"}
            width={"3rem"}
            height={"3rem"}
            fill="#fff"
            onClick={() => {
              if (ref.current) ref.current.pause();
            }}
          />
        )}

        {data && (
          <audio
            src={data}
            ref={ref}
            onTimeUpdate={(e) => {
              setTime(e.currentTarget.currentTime);
            }}
            onPlay={() => {
              setEnded(false);
              setPlaying(true);
            }}
            onPause={() => setPlaying(false)}
            onEnded={(e) => {
              setTime(0);
              setEnded(false);
              setPlaying(false);
            }}
            onLoadedData={(e) => setLength(e.currentTarget.duration)}
          />
        )}
      </div>
    </>
  );
};

const audioCSS: React.CSSProperties = {
  backgroundColor: "28282890",
  display: "flex",
  borderRadius: "2rem",
  position: "absolute",
  left: 28,
  top: 55,
  zIndex: 30,
};

const mapCss: React.CSSProperties = {
  backgroundColor: "28282890",
  display: "flex",
  borderRadius: "2rem",
  position: "absolute",
  zIndex: 14,
  bottom: 0,
  marginBottom: "20px",
  left: 58,
  transform: "translate(-50%)",
};

export default ExpOverlay;

// display: flex;
// border-radius: 2rem;
// padding: 0.25rem 0.5rem;
// gap: 20px;
// position: absolute;
// left: 50%;
// bottom: 0;
// margin-bottom: 10px;
// transform: translate(-50%);
// z-index: 89;
